import { lazy, ComponentType } from 'react';

/**
 * Props interface that all canvas widget components must implement.
 */
export interface CanvasWidgetProps {
  userAppInstance: any;
  sourceEvents: any[];
  loadingEvents: boolean;
  maxHeight: number;
  updateUserAppInstance?: (app: any) => void;
  onOpenItem?: (item: any) => void;
}

/**
 * Definition of a canvas widget type in the registry.
 */
export interface CanvasWidgetDef {
  /** Unique key — matches values in agent_def.metadata.ui.canvasTypes[] */
  canvasType: string;
  /** Display label for the canvas tab */
  label: string;
  /** MUI icon name for the canvas tab */
  icon: string;
  /** Lazy-loaded React component */
  component: React.LazyExoticComponent<ComponentType<CanvasWidgetProps>>;
  /** Tab ordering priority (higher = more left in tab bar) */
  tabPriority: number;
}

/**
 * Registry mapping canvasType string → lazy-loaded widget component.
 * When the GenericUserApp resolves canvasTypes from connected agent defs,
 * it looks up each type here to get the component to render.
 */
export const widgetRegistry: Record<string, CanvasWidgetDef> = {
  chat: {
    canvasType: 'chat',
    label: 'Conversations',
    icon: 'QuestionAnswer',
    component: lazy(() => import('./chatCanvas')),
    tabPriority: 10,
  },
  telemetryDashboard: {
    canvasType: 'telemetryDashboard',
    label: 'Dashboard',
    icon: 'Dashboard',
    component: lazy(() => import('./telemetryDashboardCanvas')),
    tabPriority: 8,
  },
  maintenanceConsole: {
    canvasType: 'maintenanceConsole',
    label: 'Console',
    icon: 'Engineering',
    component: lazy(() => import('./maintenanceConsoleCanvas')),
    tabPriority: 9,
  },
  visionInspector: {
    canvasType: 'visionInspector',
    label: 'Vision Inspector',
    icon: 'CameraAlt',
    component: lazy(() => import('./visionInspectorCanvas')),
    tabPriority: 8,
  },
  molecularViewer: {
    canvasType: 'molecularViewer',
    label: '3D Structure',
    icon: 'ViewInAr',
    component: lazy(() => import('./molecularViewerCanvas')),
    tabPriority: 8,
  },
  msaViewer: {
    canvasType: 'msaViewer',
    label: 'Sequence Alignment',
    icon: 'Biotech',
    component: lazy(() => import('./msaViewerCanvas')),
    tabPriority: 7,
  },
  genomicBrowser: {
    canvasType: 'genomicBrowser',
    label: 'Genomic Browser',
    icon: 'Science',
    component: lazy(() => import('./genomicBrowserCanvas')),
    tabPriority: 7,
  },
  drugDiscovery: {
    canvasType: 'drugDiscovery',
    label: 'Drug Discovery',
    icon: 'Science',
    component: lazy(() => import('./drugDiscoveryCanvas')),
    tabPriority: 7,
  },
  notebook: {
    canvasType: 'notebook',
    label: 'Research Notebook',
    icon: 'MenuBook',
    component: lazy(() => import('./notebookCanvas')),
    tabPriority: 5,
  },
  knowledgeGraph: {
    canvasType: 'knowledgeGraph',
    label: 'Knowledge Graph',
    icon: 'Hub',
    component: lazy(() => import('./knowledgeGraphCanvas')),
    tabPriority: 6,
  },
  quiz: {
    canvasType: 'quiz',
    label: 'Quiz',
    icon: 'Quiz',
    component: lazy(() => import('./quizCanvas')),
    tabPriority: 6,
  },
  sourceEntityViewer: {
    canvasType: 'sourceEntityViewer',
    label: 'Source Entities',
    icon: 'Dataset',
    component: lazy(() => import('./sourceEntityViewerCanvas')),
    tabPriority: 4,
  },
};
